import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import {
  type Folder,
  type FolderPlayAction,
} from '@/shared/features/quiz-folder';

export interface FolderCtaState {
  /** 서버가 접어 준 전체 재생 가능 여부. 화면은 이 값만 본다. */
  playAction: FolderPlayAction;
  /** `플랜 결제` 주 버튼 노출 여부 — 내가 결제하면 풀리는 상황에서만. */
  showPayPlan: boolean;
  /** 하단 회색 안내 문구. 없으면 `undefined`. */
  notice: string | undefined;
}

/** 상태별 안내 문구의 번역 키. 서버가 주는 상태에 1:1로 맞춘다. */
const NOTICE_KEY = {
  ENTER: undefined,
  // 서버의 옛 값. 폴더 단위 체험 재생은 하지 않는다.
  EXPERIENCE: undefined,
  PLAN_PAYMENT: 'common:folder.detail.notice.lockedMine',
  UNAVAILABLE: 'common:folder.detail.notice.unavailable',
  EMPTY: undefined,
  // CTA 는 소유자에게만 그리므로 화면에 닿지 않는다. 서버 상태와 1:1 을 지키려 값만 둔다.
  LOGIN_REQUIRED: 'common:folder.detail.notice.loginRequired',
} as const satisfies Record<FolderPlayAction, string | undefined>;

/**
 * 폴더 상세 CTA 판정.
 *
 * 전체 재생 가능 여부는 서버 `playAction` 하나로 결정한다. 목록이 20개씩 끊겨 오므로
 * 클라이언트가 `items[]` 를 훑어 판정하면 틀린다.
 *
 * ⚠️ SSR 의 첫 응답은 익명 기준이다. 로그인 사용자는 화면에서 다시 조회한다(`FolderDetailPageView`).
 */
export const useFolderCtaScenario = (folder: Folder): FolderCtaState => {
  const { t } = useTranslation(['common']);

  return useMemo(() => {
    const { playAction } = folder;
    const noticeKey = NOTICE_KEY[playAction];

    return {
      playAction,
      // 결제로 풀리는 상황은 서버가 PLAN_PAYMENT 로만 알려준다.
      // 남의 잠긴 퀴즈가 섞인 폴더는 결제해도 안 풀려 UNAVAILABLE 이 온다.
      showPayPlan: playAction === 'PLAN_PAYMENT',
      notice: noticeKey ? t(noticeKey) : undefined,
    };
  }, [folder, t]);
};
